/**
 * Subcategory Mapping Utility
 * Derives the subcategory and main category from a study name and level
 *
 * This is needed for search matching because study keys in the database include
 * the full hierarchical path: Level-MainCategory-Subcategory-StudyName
 */

import {
  HBO_ASSOCIATE_DEGREE_STUDIES,
  HBO_MASTER_STUDIES,
  UNIVERSITEIT_BACHELOR_STUDIES,
  UNIVERSITEIT_MASTER_STUDIES
} from '../../features/vacature-plaatsen/data/constants';

/**
 * Build reverse mapping: studyName → { mainCategory, subcategory }
 * @param {Object} studiesData - Hierarchical study data (mainCategory → subcategory → studies[])
 * @returns {Map} Map of studyName → { mainCategory, subcategory }
 */
function buildReverseMapping(studiesData) {
  const mapping = new Map();

  if (!studiesData || typeof studiesData !== 'object') {
    console.error('❌ Invalid studiesData provided to buildReverseMapping:', studiesData);
    return mapping;
  }

  Object.entries(studiesData).forEach(([mainCategory, subcategories]) => {
    if (!subcategories || typeof subcategories !== 'object') {
      console.error(`❌ Invalid subcategories for mainCategory "${mainCategory}":`, subcategories);
      return;
    }

    Object.entries(subcategories).forEach(([subcategory, studies]) => {
      if (!Array.isArray(studies)) {
        console.error(`❌ Studies is not an array for "${mainCategory}" > "${subcategory}":`, studies);
        return;
      }

      studies.forEach(studyName => {
        mapping.set(studyName, {
          mainCategory,
          subcategory
        });
      });
    });
  });

  return mapping;
}

// Lazy initialization - build mappings on first access
let hboAssociateDegreeMapping = null;
let hboMasterMapping = null;
let universiteitBachelorMapping = null;
let universiteitMasterMapping = null;

function ensureMappingsInitialized() {
  if (!hboAssociateDegreeMapping) {
    hboAssociateDegreeMapping = buildReverseMapping(HBO_ASSOCIATE_DEGREE_STUDIES);
    hboMasterMapping = buildReverseMapping(HBO_MASTER_STUDIES);
    universiteitBachelorMapping = buildReverseMapping(UNIVERSITEIT_BACHELOR_STUDIES);
    universiteitMasterMapping = buildReverseMapping(UNIVERSITEIT_MASTER_STUDIES);

    console.log('✅ Subcategory mappings initialized:', {
      'HBO Associate Degree': hboAssociateDegreeMapping.size,
      'HBO Master': hboMasterMapping.size,
      'Universiteit Bachelor': universiteitBachelorMapping.size,
      'Universiteit Master': universiteitMasterMapping.size
    });
  }
}

/**
 * Get the subcategory and main category for a given study name and level
 *
 * @param {string} studyName - The study name (e.g., "Finance")
 * @param {string} studyLevel - The study level (e.g., "HBO Associate Degree")
 * @returns {{ mainCategory: string, subcategory: string } | null} The category info or null if not found
 *
 * @example
 * getSubcategoryForStudy('Finance', 'HBO Associate Degree')
 * // Returns: { mainCategory: 'Economisch en Bedrijf', subcategory: 'Finance & Accountancy' }
 */
export function getSubcategoryForStudy(studyName, studyLevel) {
  if (!studyName || !studyLevel) {
    return null;
  }

  // Ensure mappings are built before accessing them
  ensureMappingsInitialized();

  let mapping = null;

  // Select the appropriate mapping based on study level
  if (studyLevel === 'HBO Associate Degree') {
    mapping = hboAssociateDegreeMapping;
  } else if (studyLevel === 'HBO Master') {
    mapping = hboMasterMapping;
  } else if (studyLevel === 'Universiteit Bachelor') {
    mapping = universiteitBachelorMapping;
  } else if (studyLevel === 'Universiteit Master') {
    mapping = universiteitMasterMapping;
  }

  // Return the mapping if found
  return mapping ? mapping.get(studyName) || null : null;
}

/**
 * Check if a study level uses subcategories (4-part key structure)
 *
 * @param {string} studyLevel - The study level
 * @returns {boolean} True if this level uses subcategories
 */
export function hasSubcategories(studyLevel) {
  return [
    'HBO Associate Degree',
    'HBO Master',
    'Universiteit Bachelor',
    'Universiteit Master'
  ].includes(studyLevel);
}
